/**
 * Host Bridge - tool_project 与宿主（H5 iframe 父页面）通信
 *
 * 统一处理三种宿主环境：
 * 1. App WebView → 通过 window.AppInterface（已有 appBridge 处理）
 * 2. H5 iframe  → 通过 postMessage 双向通信
 * 3. 浏览器独立访问 → 不需要通信
 *
 * 使用示例：
 * ```typescript
 * import { hostBridge } from '@/utils/hostBridge'
 *
 * // 检测宿主类型
 * const type = hostBridge.getHostType() // 'app' | 'h5' | 'browser'
 *
 * // 请求宿主登录
 * hostBridge.requestLogin('/tactical-board/jz')
 *
 * // 监听宿主消息
 * hostBridge.on('LOGIN_SUCCESS', (payload) => {
 *   console.log('登录成功:', payload.token)
 * })
 * ```
 */

export type HostType = 'app' | 'h5' | 'browser'

export interface HostMessage {
    type: string
    payload?: any
    source?: string // 发送方标识
}

type MessageHandler = (payload: any) => void

class HostBridge {
    private handlers: Map<string, MessageHandler[]> = new Map()
    private hostOrigin: string = '*' // 生产环境应设置为具体的宿主域名
    private _hostType: HostType | null = null

    constructor() {
        if (typeof window !== 'undefined') {
            window.addEventListener('message', this.onMessage)
        }
    }

    // ==================== 环境检测 ====================

    /**
     * 检测当前宿主类型
     * - app: 在 Flutter/RN App 的 WebView 中
     * - h5: 被 H5 宿主通过 iframe 嵌入
     * - browser: 独立浏览器访问
     */
    getHostType(): HostType {
        if (this._hostType) return this._hostType

        // 优先通过 URL 参数判断（最可靠，由宿主显式指定）
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search)
            const from = urlParams.get('from')
            if (from === 'app') {
                this._hostType = 'app'
                return 'app'
            }
            if (from === 'h5') {
                this._hostType = 'h5'
                return 'h5'
            }
        }

        // App 环境：存在 AppInterface
        if (typeof window !== 'undefined' && window.AppInterface) {
            this._hostType = 'app'
            return 'app'
        }

        // iframe 内（被 H5 嵌入）
        if (this.isInIframe()) {
            this._hostType = 'h5'
            return 'h5'
        }

        // 独立浏览器访问
        this._hostType = 'browser'
        return 'browser'
    }

    /** 是否在 iframe 中 */
    isInIframe(): boolean {
        try {
            return typeof window !== 'undefined' && window.self !== window.top
        } catch {
            return true // 跨域 iframe 访问 window.top 会报错，说明确实在 iframe 中
        }
    }

    /** 是否在 App WebView 中 */
    isInApp(): boolean {
        return this.getHostType() === 'app'
    }

    /** 是否由宿主嵌入（App 或 H5），而非独立访问 */
    isEmbedded(): boolean {
        return this.getHostType() !== 'browser'
    }

    // ==================== 向宿主发送消息 ====================

    /** 向宿主发送 postMessage */
    sendToHost(type: string, payload?: any): void {
        const message: HostMessage = {
            type,
            payload,
            source: 'tool_project',
        }

        if (this.isInIframe() && window.parent) {
            window.parent.postMessage(message, this.hostOrigin)
            console.log('[HostBridge] 向宿主发送消息:', type, payload)
        }
    }

    /** 请求宿主执行登录 */
    requestLogin(redirectPath?: string): void {
        this.sendToHost('NEED_LOGIN', { redirectPath })
    }

    /** 通知宿主进行页面导航（跳转到非工具页面） */
    navigateHost(url: string): void {
        this.sendToHost('NAVIGATE', { url })
    }

    /** 通知宿主关闭 iframe / WebView */
    closeIframe(): void {
        this.sendToHost('CLOSE')
    }

    /** 通知宿主更新页面标题 */
    updateTitle(title: string): void {
        this.sendToHost('UPDATE_TITLE', { title })
    }

    /** 通知宿主更新 iframe 高度（自适应） */
    updateHeight(height: number): void {
        this.sendToHost('UPDATE_HEIGHT', { height })
    }

    // ==================== 监听宿主消息 ====================

    /** 注册消息处理器 */
    on(type: string, handler: MessageHandler): void {
        if (!this.handlers.has(type)) {
            this.handlers.set(type, [])
        }
        this.handlers.get(type)!.push(handler)
    }

    /** 移除消息处理器 */
    off(type: string, handler: MessageHandler): void {
        const handlers = this.handlers.get(type)
        if (handlers) {
            const index = handlers.indexOf(handler)
            if (index > -1) handlers.splice(index, 1)
        }
    }

    /** 处理接收到的消息（箭头函数保持 this 绑定） */
    private onMessage = (event: MessageEvent): void => {
        const data = event.data as HostMessage
        if (!data || !data.type) return

        // 忽略自己发送的消息，以及 App.vue 内部转发的消息（防止递归）
        if (data.source === 'tool_project' || data.source === 'app_relay') return

        // 安全检查：生产环境应验证 event.origin
        // if (this.hostOrigin !== '*' && event.origin !== this.hostOrigin) return

        console.log('[HostBridge] 收到宿主消息:', data.type, data.payload)

        const handlers = this.handlers.get(data.type)
        if (handlers) {
            handlers.forEach(handler => {
                try {
                    handler(data.payload)
                } catch (error) {
                    console.error(`[HostBridge] 处理消息 ${data.type} 出错:`, error)
                }
            })
        }
    }

    // ==================== 配置 ====================

    /** 设置允许通信的宿主域名（安全配置） */
    setHostOrigin(origin: string): void {
        this.hostOrigin = origin
    }

    /** 重置缓存的宿主类型（用于测试） */
    resetHostType(): void {
        this._hostType = null
    }

    /** 销毁，清理事件监听 */
    destroy(): void {
        if (typeof window !== 'undefined') {
            window.removeEventListener('message', this.onMessage)
        }
        this.handlers.clear()
    }
}

// 导出单例
export const hostBridge = new HostBridge()

// 默认导出
export default hostBridge
